import { execAdmin } from '@src/apis/reware/utils';
import type {
  GenerateResalePricingExportMutation,
  GenerateResalePricingExportMutationVariables,
} from '@src/types/__generated/rewareAdminTypes';

import type { PricingExportResult } from './types';

const gql = String.raw;

export interface GenerateResalePricingExportArgs {
  partnerId: number;
  id: number;
}

/**
 * Requests a TSV export of a pricing snapshot's rules.
 *
 * Returns a presigned URL plus its `expiresAt`. The caller (data provider →
 * component) opens the URL in a new tab so the browser downloads directly — the
 * TSV bytes never pass through the GraphQL response or React state.
 *
 * Server-side details (where the bytes are staged, the URL's TTL, how the
 * filename is built) are owned by reware-api and intentionally not asserted
 * here, since this frontend can't verify them.
 *
 * @param id - The `PricingDiscount.id` of the snapshot to export.
 */
export const generateResalePricingExport = async ({
  partnerId,
  id,
}: GenerateResalePricingExportArgs): Promise<PricingExportResult> => {
  const mutation = gql`
    mutation generateResalePricingExport($partnerId: Int!, $id: Int!) {
      partner(id: $partnerId) {
        generateResalePricingExport(id: $id) {
          url
          expiresAt
        }
      }
    }
  `;

  const data = await execAdmin<
    GenerateResalePricingExportMutationVariables,
    GenerateResalePricingExportMutation
  >(mutation, { variables: { partnerId, id } });

  const result = data.partner?.generateResalePricingExport;
  if (!result) {
    throw new Error(
      `generateResalePricingExport: unexpected response — partner.generateResalePricingExport is ${
        result === null ? 'null' : 'undefined'
      } (partnerId=${partnerId}, id=${id})`,
    );
  }

  return result;
};
